import type { Infer } from 'superstruct';
import {
	any,
	array,
	boolean,
	nullable,
	number,
	object,
	optional,
	string,
} from 'superstruct';

// Age range
export const GoogleAdsAgeRange = object({
	min_age: optional(number()),
});

export type GoogleAdsAgeRange = Infer<typeof GoogleAdsAgeRange>;

// Age dimension
export const GoogleAdsAgeDimension = object({
	age_ranges: optional(array(GoogleAdsAgeRange)),
	include_undetermined: optional(boolean()),
});

export type GoogleAdsAgeDimension = Infer<typeof GoogleAdsAgeDimension>;

// Gender dimension
export const GoogleAdsGenderDimension = object({
	genders: optional(array(any())),
	include_undetermined: optional(boolean()),
});

export type GoogleAdsGenderDimension = Infer<typeof GoogleAdsGenderDimension>;

// Household income dimension sub-DTO
export const GoogleAdsHouseholdIncomeDimension = object({
	income_ranges: optional(array(any())),
	include_undetermined: optional(boolean()),
});

export type GoogleAdsHouseholdIncomeDimension = Infer<
	typeof GoogleAdsHouseholdIncomeDimension
>;

// Parental status dimension
export const GoogleAdsParentalStatusDimension = object({
	parental_statuses: optional(array(any())),
	include_undetermined: optional(boolean()),
});

export type GoogleAdsParentalStatusDimension = Infer<
	typeof GoogleAdsParentalStatusDimension
>;

// User interest
export const GoogleAdsUserInterest = object({
	user_interest_category: optional(string()),
	name: optional(string()),
	user_interest_id: optional(number()),
});

export type GoogleAdsUserInterest = Infer<typeof GoogleAdsUserInterest>;

// Custom audience
export const GoogleAdsCustomAudience = object({
	custom_audience: optional(string()),
	name: optional(string()),
	id: optional(number()),
});

export type GoogleAdsCustomAudience = Infer<typeof GoogleAdsCustomAudience>;

// User list segment
export const GoogleAdsUserListSegment = object({
	user_list: optional(string()),
});

export type GoogleAdsUserListSegment = Infer<typeof GoogleAdsUserListSegment>;

// Life event segment
export const GoogleAdsLifeEventSegment = object({
	life_event: optional(string()),
});

export type GoogleAdsLifeEventSegment = Infer<typeof GoogleAdsLifeEventSegment>;

// Detailed demographic segment
export const GoogleAdsDetailedDemographicSegment = object({
	detailed_demographic: optional(string()),
});

export type GoogleAdsDetailedDemographicSegment = Infer<
	typeof GoogleAdsDetailedDemographicSegment
>;

// Segment
export const GoogleAdsSegment = object({
	user_list: optional(GoogleAdsUserListSegment),
	user_interest: optional(GoogleAdsUserInterest),
	life_event: optional(GoogleAdsLifeEventSegment),
	detailed_demographic: optional(GoogleAdsDetailedDemographicSegment),
	custom_audience: optional(GoogleAdsCustomAudience),
});

export type GoogleAdsSegment = Infer<typeof GoogleAdsSegment>;

// Audience segments
export const GoogleAdsAudienceSegments = object({
	segments: optional(array(GoogleAdsSegment)),
});

export type GoogleAdsAudienceSegments = Infer<typeof GoogleAdsAudienceSegments>;

// Dimension
export const GoogleAdsDimension = object({
	age: optional(GoogleAdsAgeDimension),
	gender: optional(GoogleAdsGenderDimension),
	household_income: optional(GoogleAdsHouseholdIncomeDimension),
	parental_status: optional(GoogleAdsParentalStatusDimension),
	audience_segments: optional(GoogleAdsAudienceSegments),
});

export type GoogleAdsDimension = Infer<typeof GoogleAdsDimension>;

// Audience
export const GoogleAdsAudience = object({
	id: optional(number()),
	resource_name: optional(string()),
	name: optional(string()),
	status: optional(number()),
	description: optional(nullable(string())),
	dimensions: optional(array(GoogleAdsDimension)),
	exclusion_dimension: optional(nullable(string())),
});

export type GoogleAdsAudience = Infer<typeof GoogleAdsAudience>;

export const GoogleAdsAudienceSegment = object({
	audience: optional(GoogleAdsAudience),
});

export type GoogleAdsAudienceSegment = Infer<typeof GoogleAdsAudienceSegment>;
